<template>
  <div class="two-factor-verification">
    <div class="code-input-container" :class="{ 'has-error': error }">
      <InputOtp v-model="code" :length="6" :disabled="isVerifying" integerOnly />
    </div>

    <Transition name="fade">
      <Message
        v-if="error"
        severity="error"
        :closable="false"
        class="tfa-message"
      >
        {{ error }}
      </Message>
    </Transition>

    <Transition name="fade">
      <Message
        v-if="isRateLimited && !error"
        severity="warn"
        :closable="false"
        class="tfa-message"
      >
        Please wait before requesting a new code.
      </Message>
    </Transition>

    <div v-if="isVerifying" class="verifying-state">
      <ProgressSpinner style="width: 22px; height: 22px" strokeWidth="4" />
      <span>Verifying…</span>
    </div>

    <div class="resend-row">
      <span class="resend-prompt">Didn't get it?</span>
      <Button
        :label="resendLabel"
        link
        @click="resendCode"
        :disabled="isResending || isRateLimited || resendCooldown > 0"
        :loading="isResending"
        class="resend-button"
      />
    </div>

    <div class="back-container">
      <Button label="Cancel" link icon="pi pi-arrow-left" @click="$emit('cancel')" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import Button from 'primevue/button'
import InputOtp from 'primevue/inputotp'
import Message from 'primevue/message'
import ProgressSpinner from 'primevue/progressspinner'

defineProps<{
  maskedEmail: string
  isVerifying?: boolean
}>()

const emit = defineEmits<{
  (e: 'verify', code: string): void
  (e: 'resend'): void
  (e: 'cancel'): void
}>()

const code = ref('')
const error = ref<string | null>(null)
const isResending = ref(false)
const isRateLimited = ref(false)
const resendCooldown = ref(0)

let cooldownInterval: ReturnType<typeof setInterval> | null = null

const resendLabel = computed(() =>
  resendCooldown.value > 0 ? `Resend in ${resendCooldown.value}s` : 'Resend code',
)

const verifyCode = () => {
  if (code.value.length !== 6) return
  error.value = null
  emit('verify', code.value)
}

const resendCode = () => {
  if (isResending.value || isRateLimited.value || resendCooldown.value > 0) return
  error.value = null
  isResending.value = true
  emit('resend')
}

const startResendCooldown = () => {
  resendCooldown.value = 60
  cooldownInterval = setInterval(() => {
    if (resendCooldown.value > 0) {
      resendCooldown.value--
    } else {
      if (cooldownInterval) {
        clearInterval(cooldownInterval)
        cooldownInterval = null
      }
    }
  }, 1000)
}

watch(code, (newCode) => {
  if (newCode.length === 6) {
    verifyCode()
  }
})

const setError = (errorMessage: string | null) => {
  error.value = errorMessage
}

const setRateLimited = (limited: boolean) => {
  isRateLimited.value = limited
}

const setResending = (resending: boolean) => {
  isResending.value = resending
  if (!resending && !isRateLimited.value) {
    startResendCooldown()
  }
}

const clearCode = () => {
  code.value = ''
}

defineExpose({
  setError,
  setRateLimited,
  setResending,
  clearCode,
  startResendCooldown,
})

onUnmounted(() => {
  if (cooldownInterval) {
    clearInterval(cooldownInterval)
  }
})
</script>

<style scoped>
.two-factor-verification {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 0.85rem;
}

.code-input-container {
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 0.25rem;
}

.code-input-container :deep(.p-inputotp) {
  gap: 0.5rem;
}

.code-input-container :deep(.p-inputotp-input) {
  width: 2.75rem;
  height: 3.25rem;
  font-size: 1.375rem;
  font-weight: 600;
  text-align: center;
  border-radius: 10px;
  background: var(--p-surface-50, rgba(103, 58, 183, 0.03));
  border: 1px solid var(--p-content-border-color, rgba(0, 0, 0, 0.12));
  transition:
    border-color 0.15s ease,
    background-color 0.15s ease,
    box-shadow 0.15s ease;
}

.code-input-container :deep(.p-inputotp-input:hover) {
  border-color: var(--primary-color, #673ab7);
}

.code-input-container :deep(.p-inputotp-input:focus) {
  outline: none;
  border-color: var(--primary-color, #673ab7);
  background: var(--p-content-background, #ffffff);
  box-shadow: 0 0 0 3px var(--primary-color-transparent, rgba(103, 58, 183, 0.18));
}

.code-input-container.has-error :deep(.p-inputotp-input) {
  border-color: var(--p-red-500, #ef4444);
  background: rgba(239, 68, 68, 0.04);
}

.code-input-container.has-error :deep(.p-inputotp-input:focus) {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}

.tfa-message {
  width: 100%;
  margin: 0;
}

.verifying-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--p-text-muted-color);
  font-size: 0.85rem;
}

.resend-row {
  display: flex;
  align-items: baseline;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--p-text-muted-color);
  margin-top: 0.25rem;
}

.resend-button :deep(.p-button) {
  padding: 0;
  min-width: auto;
}

.resend-button :deep(.p-button-label) {
  font-weight: 500;
}

.back-container {
  margin-top: -0.35rem;
}

.back-container :deep(.p-button) {
  padding: 0.35rem 0.5rem;
  font-size: 0.85rem;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
